(() => {
  const section = document.querySelector('.collection-section[id^="shopify-section-"]');
  if (!section) return;

  const sectionId = section.id.replace(/^shopify-section-/, '');
  const cache = new Map();
  const queue = [];
  let running = 0;
  let requestId = 0;
  let activeKey = keyFor(window.location.href);

  function keyFor(path) {
    const url = new URL(path, window.location.href);
    ['preview_theme_id', 'sections', 'section_id'].forEach((name) => url.searchParams.delete(name));
    return url.pathname + url.search;
  }

  function requestUrl(path) {
    const url = new URL(path, window.location.href);
    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
    url.searchParams.set('sections', sectionId);
    return url;
  }

  function pumpQueue() {
    while (running < 4 && queue.length) {
      const task = queue.shift();
      task.state = 'loading';
      running += 1;
      fetch(requestUrl(task.path), { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
          return response.json();
        })
        .then((sections) => {
          const html = sections[sectionId];
          if (!html) throw new Error('Collection section was not returned');
          task.state = 'ready';
          task.html = html;
          task.resolve(html);
        })
        .catch((error) => {
          cache.delete(task.key);
          task.reject(error);
        })
        .finally(() => {
          running -= 1;
          pumpQueue();
        });
    }
  }

  function prepare(path, priority = false) {
    const key = keyFor(path);
    const existing = cache.get(key);
    if (existing) {
      if (priority && existing.state === 'queued') {
        const index = queue.indexOf(existing);
        if (index > 0) queue.unshift(...queue.splice(index, 1));
        pumpQueue();
      }
      return existing.state === 'ready' ? Promise.resolve(existing.node || existing.html) : existing.promise;
    }
    let resolve;
    let reject;
    const promise = new Promise((ready, failed) => {
      resolve = ready;
      reject = failed;
    });
    const task = { key, path, promise, resolve, reject, state: 'queued' };
    cache.set(key, task);
    if (priority) queue.unshift(task);
    else queue.push(task);
    pumpQueue();
    return promise;
  }

  function prefetch(paths, priority = false) {
    paths.forEach((path) => {
      if (keyFor(path) !== activeKey) prepare(path, priority).catch(() => {});
    });
  }

  function prefetchNearby(collection) {
    const select = collection.querySelector('select[is="sub-collections"]');
    if (select) prefetch([...select.options].map((option) => option.value), true);
  }

  function installCollection(content, targetKey, source, scrollToTop) {
    const nextCollection = typeof content === 'string'
      ? new DOMParser().parseFromString(content, 'text/html').querySelector('.collection')
      : content;
    if (!nextCollection || !nextCollection.querySelector('#ProductGridContainer')) {
      throw new Error('Collection products were not returned');
    }
    const targetUrl = new URL(targetKey, window.location.href);
    nextCollection.querySelectorAll('form[is="facet-form"]').forEach((form) => {
      form.setAttribute('action', targetUrl.pathname);
      form.removeAttribute('data-history');
    });
    nextCollection.querySelectorAll('.pagination a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.href);
      url.pathname = targetUrl.pathname;
      link.setAttribute('href', url.pathname + url.search);
    });
    const categorySelect = nextCollection.querySelector('select[is="sub-collections"]');
    if (categorySelect) categorySelect.value = categorySelect.dataset.currentUrl;
    const current = section.querySelector('.collection');
    current.removeAttribute('aria-busy');
    cache.set(activeKey, { state: 'ready', node: current });
    current.replaceWith(nextCollection);
    activeKey = targetKey;
    cache.set(targetKey, { state: 'ready', node: nextCollection });
    document.dispatchEvent(new CustomEvent('collection:reloaded'));
    if (!window.location.pathname.endsWith('/collections/all')) prefetchNearby(nextCollection);

    if (scrollToTop) nextCollection.scrollIntoView({ block: 'start', behavior: 'auto' });
    else if (source === 'link') {
      nextCollection.querySelector('.subcollections-nav .active a, .subcollections__back')?.focus({ preventScroll: true });
    }
  }

  async function switchCollection(path, source, scrollToTop = false) {
    const url = new URL(path, window.location.href);
    if (url.origin !== window.location.origin) return;
    const targetKey = keyFor(path);
    if (targetKey === activeKey) return;
    const thisRequest = ++requestId;
    const current = section.querySelector('.collection');
    const message = current?.querySelector('.collection-nav-error');
    if (!current) return;
    if (message) message.hidden = true;

    const cached = cache.get(targetKey);
    if (cached?.state === 'ready') {
      installCollection(cached.node || cached.html, targetKey, source, scrollToTop);
      return;
    }
    current.setAttribute('aria-busy', 'true');
    try {
      const html = await prepare(path, true);
      if (thisRequest !== requestId) return;
      installCollection(html, targetKey, source, scrollToTop);
    } catch (error) {
      if (thisRequest !== requestId) return;
      console.error(error);
      if (message) message.hidden = false;
      if (source === 'select') {
        const select = current.querySelector('select[is="sub-collections"]');
        if (select) select.value = select.dataset.currentUrl;
      }
      current.removeAttribute('aria-busy');
    }
  }

  const currentCollection = section.querySelector('.collection');
  if (currentCollection) cache.set(activeKey, { state: 'ready', node: currentCollection });
  section.querySelectorAll('template[data-initial-collection]').forEach((template) => {
    const collection = template.content.querySelector('.collection');
    if (collection) cache.set(keyFor(template.dataset.initialCollection), { state: 'ready', node: collection });
    template.remove();
  });
  if (currentCollection && !window.location.pathname.endsWith('/collections/all')) {
    prefetchNearby(currentCollection);
  }

  document.addEventListener('pointerover', (event) => {
    const link = event.target.closest('.collection-section .subcollections-nav a');
    if (link?.closest('.collection-section') === section) prepare(link.href, true).catch(() => {});
  });
  document.addEventListener('focusin', (event) => {
    const link = event.target.closest('.collection-section .subcollections-nav a');
    if (link?.closest('.collection-section') === section) prepare(link.href, true).catch(() => {});
  });
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.collection-section .subcollections-nav a, .collection-section .pagination a');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target || link.download) return;
    if (link.closest('.collection-section') !== section) return;
    event.preventDefault();
    switchCollection(link.href, 'link', Boolean(link.closest('.pagination')));
  });
  document.addEventListener('change', (event) => {
    const select = event.target.closest('.collection-section select[is="sub-collections"]');
    if (!select?.value || select.closest('.collection-section') !== section) return;
    event.stopImmediatePropagation();
    switchCollection(select.value, 'select');
  }, true);
})();
